import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CdkMenu, CdkMenuItem, CdkMenuTrigger } from '@angular/cdk/menu';
import { finalize, map } from 'rxjs';
import { downloadCsv } from '../../core/functions/csv-export';
import { AuthService } from '../../core/services/auth/auth.service';
import { Release } from '../../models/release';
import { TeamMember } from '../../models/team-member';
import { TestPlan, TestPlanStatus } from '../../models/test-plan';
import { ReleaseService } from '../../services/release/release.service';
import { TestCaseService } from '../../services/test-case/test-case.service';
import { ReleaseViewStateService } from '../../services/view-state/release-view-state.service';
import { SelectComponent, ToastService } from '../../ui';

type StatusFilter = 'All' | 'InProgress' | 'Failing' | 'NotStarted' | 'Completed';
type SeverityFilter = 'Any' | 'Showstopper' | 'Blocker' | 'Functional' | 'Cosmetic';

/**
 * Release Home — manager view (PBI 55096). Lead/Admin only. Shows every plan in a
 * flat list with Assignee / Status / Severity dropdown filters and full reassignment.
 */
@Component({
  selector: 'app-manager-release-home',
  standalone: true,
  imports: [CommonModule, FormsModule, CdkMenuTrigger, CdkMenu, CdkMenuItem, SelectComponent],
  templateUrl: './manager-release-home.component.html',
  styleUrl: './manager-release-home.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManagerReleaseHomeComponent {
  private auth = inject(AuthService);
  private releaseService = inject(ReleaseService);
  private testCaseService = inject(TestCaseService);
  private viewState = inject(ReleaseViewStateService);
  private router = inject(Router);
  private toast = inject(ToastService);
  private route = inject(ActivatedRoute);

  /** Plan ids whose test cases are being fetched before jumping into execution. */
  private readonly openingIds = signal<Set<number>>(new Set());

  readonly release = toSignal(
    this.route.data.pipe(map((d) => d['releaseData'] as Release | null)),
    {
      initialValue: null,
    },
  );

  readonly users = signal<TeamMember[]>([]);
  readonly search = signal('');
  readonly assigneeFilter = signal<string>('any'); // 'any' | 'unassigned' | userId
  readonly statusFilter = signal<StatusFilter>('All');
  readonly severityFilter = signal<SeverityFilter>('Any');

  /** Per-row popover people search (only one popover is open at a time). */
  popoverSearch = '';

  /** Plan row briefly highlighted after clicking the banner "View →" (AC3). */
  readonly highlightedPlanId = signal<number | null>(null);

  private readonly overrides = signal<Record<number, Partial<TestPlan>>>({});

  readonly currentUserId = computed(() => this.auth.userSig()?.userId ?? null);

  constructor() {
    const releaseId = this.release()?.releaseId;
    if (releaseId != null) this.search.set(this.viewState.getSearch(releaseId));

    this.releaseService.getAssignableUsers().subscribe({
      next: (users) => this.users.set(users),
      error: () => this.toast.error('Failed to load team members.'),
    });
  }

  readonly plans = computed<TestPlan[]>(() => {
    const r = this.release();
    const base = r ? (r.suites ?? []).flatMap((s) => s.testPlanSummaries ?? []) : [];
    const ov = this.overrides();
    return base.map((p) => (ov[p.testPlanId] ? { ...p, ...ov[p.testPlanId] } : p));
  });

  readonly filteredPlans = computed(() => {
    const term = this.search().trim().toLowerCase();
    const assignee = this.assigneeFilter();
    const status = this.statusFilter();
    const severity = this.severityFilter();
    return this.plans()
      .filter((p) => !term || p.testPlanName.toLowerCase().includes(term))
      .filter((p) => matchesAssignee(p, assignee))
      .filter((p) => matchesStatus(p.status, status))
      .filter((p) => matchesSeverity(p, severity));
  });

  /** View-toggle counts. Kept identical to the tester view's so the numbers on the
      chips do not jump when the user switches between the two pages (Bug 56799). */
  readonly myViewCount = computed(
    () =>
      this.plans().filter(
        (p) => p.assignedUserId === this.currentUserId() || p.assignedUserId == null,
      ).length,
  );

  readonly everyoneCount = computed(() => this.plans().length);

  // ---- filter option lists with counts ----

  readonly assigneeOptions = computed(() => {
    const plans = this.plans();
    const meId = this.currentUserId();
    const opts: { value: string; label: string }[] = [
      { value: 'any', label: `Anyone (${plans.length})` },
    ];
    for (const u of this.users()) {
      const me = u.userId === meId ? ' (me)' : '';
      opts.push({ value: String(u.userId), label: `${u.firstName} ${u.lastName}${me}` });
    }
    const unassigned = plans.filter((p) => p.assignedUserId == null).length;
    opts.push({ value: 'unassigned', label: `Unassigned (${unassigned})` });
    return opts;
  });

  readonly statusOptions = computed(() => {
    const p = this.plans();
    const c = (s: TestPlanStatus) => p.filter((x) => x.status === s).length;
    return [
      { value: 'All' as StatusFilter, label: `All (${p.length})` },
      { value: 'InProgress' as StatusFilter, label: `In progress (${c('InProgress')})` },
      { value: 'Failing' as StatusFilter, label: `Failing (${c('Failing')})` },
      { value: 'NotStarted' as StatusFilter, label: `Not started (${c('NotStarted')})` },
      { value: 'Completed' as StatusFilter, label: `Complete (${c('Completed')})` },
    ];
  });

  readonly severityOptions = computed(() => {
    const p = this.plans();
    const sum = (sel: (x: TestPlan) => number) => p.reduce((acc, x) => acc + sel(x), 0);
    return [
      { value: 'Any' as SeverityFilter, label: 'Any' },
      {
        value: 'Showstopper' as SeverityFilter,
        label: `Showstopper (${sum((x) => x.numberOfShowstoppers)})`,
      },
      { value: 'Blocker' as SeverityFilter, label: `Blocker (${sum((x) => x.numberOfBlockers)})` },
      {
        value: 'Functional' as SeverityFilter,
        label: `Functional (${sum((x) => x.numberOfFunctional)})`,
      },
      {
        value: 'Cosmetic' as SeverityFilter,
        label: `Cosmetic (${sum((x) => x.numberOfCosmetic)})`,
      },
    ];
  });

  // Note: the "Failing" status option uses value 'Failing' which is not in StatusFilter's
  // union for the select binding; handled by matchesStatus accepting the string.

  readonly stats = computed(() => {
    const r = this.release();
    const passed = r?.numberOfPassingTests ?? 0;
    const failed = r?.numberOfFailingTests ?? 0;
    const total = r?.numberOfTests ?? 0;
    const complete = passed + failed;
    const remaining = Math.max(0, total - complete);
    const pct = total > 0 ? Math.round((complete / total) * 100) : 0;
    const failPct = total > 0 ? Math.round((failed / total) * 100) : 0;
    return { passed, failed, total, complete, remaining, pct, failPct };
  });

  readonly showstopperPlan = computed(
    () => this.plans().find((p) => p.numberOfShowstoppers > 0) ?? null,
  );

  readonly showstopper = computed(() => {
    const count = this.release()?.numberOfShowstoppers ?? 0;
    return count > 0 ? { count } : null;
  });

  // ---- per-plan presentation ----

  isMine(p: TestPlan): boolean {
    return p.assignedUserId != null && p.assignedUserId === this.currentUserId();
  }

  statusClass(p: TestPlan): string {
    switch (p.status) {
      case 'Completed':
        return 'done';
      case 'Failing':
        return 'failing';
      case 'InProgress':
        return 'partial';
      default:
        return 'untested';
    }
  }

  statusSub(p: TestPlan): string {
    const label =
      p.status === 'NotStarted'
        ? 'Not Started'
        : p.status === 'InProgress'
          ? 'In Progress'
          : p.status;
    if (p.numberOfFailingTests > 0) {
      const n = p.numberOfFailingTests;
      return `${label} · ${n} failure${n === 1 ? '' : 's'} flagged`;
    }
    return label;
  }

  cta(p: TestPlan): string {
    return p.status === 'Completed' ? 'Review' : 'Open';
  }

  progressPct(p: TestPlan): number {
    return p.numberOfTests > 0 ? Math.round((p.numberOfPassingTests / p.numberOfTests) * 100) : 0;
  }

  failPct(p: TestPlan): number {
    return p.numberOfTests > 0 ? Math.round((p.numberOfFailingTests / p.numberOfTests) * 100) : 0;
  }

  initials(name: string | null | undefined): string {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?';
  }

  /** Team members matching the popover search. */
  popoverPeople(): TeamMember[] {
    const term = this.popoverSearch.trim().toLowerCase();
    return this.users().filter(
      (u) => !term || `${u.firstName} ${u.lastName}`.toLowerCase().includes(term),
    );
  }

  // ---- actions ----

  /** Back to the tester "My view" page. Uses the injected Router rather than a
      routerLink so the chip stays a plain button alongside its active sibling. */
  goToMyView(): void {
    const releaseId = this.release()?.releaseId;
    if (releaseId == null) return;
    // Recorded before navigating: leads default to this view, so without the
    // override the release-home guard would send them straight back (Bug 56800).
    this.viewState.setViewOverride('mine');
    this.router.navigate(['/releases', releaseId]);
  }

  /** Search is persisted per release so switching back to My view keeps the term
      instead of silently resetting it (Bug 56799). */
  setSearch(term: string): void {
    this.search.set(term);
    const releaseId = this.release()?.releaseId;
    if (releaseId != null) this.viewState.setSearch(releaseId, term);
  }

  setAssigneeFilter(v: string): void {
    this.assigneeFilter.set(v);
  }
  setStatusFilter(v: string): void {
    this.statusFilter.set(v as StatusFilter);
  }
  setSeverityFilter(v: string): void {
    this.severityFilter.set(v as SeverityFilter);
  }

  isOpening(p: TestPlan): boolean {
    return this.openingIds().has(p.testPlanId);
  }

  /** Open a plan directly into the Execute Test Case screen (PBI 55100), resuming at
      the first un-run test case; the stepper handles the rest. */
  openPlan(p: TestPlan): void {
    const releaseId = this.release()?.releaseId;
    if (releaseId == null || this.isOpening(p)) return;

    this.viewState.setSelectedPlanId(releaseId, p.testPlanId);
    this.openingIds.update((s) => new Set(s).add(p.testPlanId));

    this.testCaseService
      .getByTestPlanId(p.testPlanId)
      .pipe(
        finalize(() =>
          this.openingIds.update((s) => {
            const next = new Set(s);
            next.delete(p.testPlanId);
            return next;
          }),
        ),
      )
      .subscribe({
        next: (cases) => {
          if (!cases.length) {
            this.toast.error('This plan has no test cases yet.');
            return;
          }
          const target = cases.find((c) => c.isPassing == null) ?? cases[0];
          this.router.navigate(['/releases', releaseId, 'test-case', target.testCaseId], {
            queryParams: { planId: p.testPlanId },
          });
        },
        error: () => this.toast.error('Could not open this plan. Please try again.'),
      });
  }

  scrollToShowstopper(): void {
    const p = this.showstopperPlan();
    if (!p) return;
    document
      .getElementById(`plan-${p.testPlanId}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    this.highlightedPlanId.set(p.testPlanId);
    setTimeout(() => this.highlightedPlanId.set(null), 2500);
  }

  reassign(p: TestPlan, member: TeamMember): void {
    const actingUserId = this.currentUserId();
    if (actingUserId == null) return;
    this.releaseService.reassignPlan(p.testPlanId, member.userId, actingUserId).subscribe({
      next: (updated) => {
        this.applyOverride(p.testPlanId, {
          assignedUserId: updated.assignedUserId ?? member.userId,
          assignedUserName: updated.assignedUserName ?? `${member.firstName} ${member.lastName}`,
        });
        this.toast.success(
          member.userId === actingUserId
            ? 'Plan assigned to you'
            : `Plan reassigned to ${member.firstName} ${member.lastName}`,
        );
      },
      error: () => this.toast.error('Failed to reassign the plan.'),
    });
  }

  unassign(p: TestPlan): void {
    const actingUserId = this.currentUserId();
    if (actingUserId == null) return;
    this.releaseService.unassignPlan(p.testPlanId, actingUserId).subscribe({
      next: () => {
        this.applyOverride(p.testPlanId, { assignedUserId: null, assignedUserName: null });
        this.toast.success('Plan unassigned');
      },
      error: () => this.toast.error('Failed to unassign the plan.'),
    });
  }

  /** Export the currently filtered plan list (AC2, AC4). */
  exportCsv(): void {
    const rows = [
      ['Plan name', 'Status', 'Progress (X/Y)', 'Assignee', 'Failures'],
      ...this.filteredPlans().map((p) => [
        p.testPlanName,
        p.status,
        `${p.numberOfPassingTests}/${p.numberOfTests}`,
        p.assignedUserName ?? 'Unassigned',
        String(p.numberOfFailingTests),
      ]),
    ];
    downloadCsv(rows, this.release()?.releaseName, 'plans');
  }

  trackByPlanId = (_: number, p: TestPlan) => p.testPlanId;
  trackByUserId = (_: number, u: TeamMember) => u.userId;

  private applyOverride(testPlanId: number, patch: Partial<TestPlan>): void {
    this.overrides.update((o) => ({ ...o, [testPlanId]: { ...o[testPlanId], ...patch } }));
  }
}

function matchesAssignee(p: TestPlan, filter: string): boolean {
  if (filter === 'any') return true;
  if (filter === 'unassigned') return p.assignedUserId == null;
  return p.assignedUserId === Number(filter);
}

function matchesStatus(status: TestPlanStatus, filter: string): boolean {
  if (filter === 'All') return true;
  return status === filter;
}

function matchesSeverity(p: TestPlan, filter: SeverityFilter): boolean {
  switch (filter) {
    case 'Showstopper':
      return p.numberOfShowstoppers > 0;
    case 'Blocker':
      return p.numberOfBlockers > 0;
    case 'Functional':
      return p.numberOfFunctional > 0;
    case 'Cosmetic':
      return p.numberOfCosmetic > 0;
    default:
      return true;
  }
}
